import { Plus, AlertTriangle } from 'lucide-react';
import type { KBNode, DetectedTool, SdlcStage } from '../types';

interface ToolCardProps {
  node: KBNode;
  det: DetectedTool;
  stage: SdlcStage;
  selected: boolean;
  dimmed: boolean;
  onEnter: (e: React.MouseEvent) => void;
  onLeave: () => void;
  onClick: () => void;
  refCb: (el: HTMLDivElement | null) => void;
}

export function ToolCard({
  node,
  det,
  stage,
  selected,
  dimmed,
  onEnter,
  onLeave,
  onClick,
  refCb,
}: ToolCardProps) {
  const tierLabel = det.tier === 'possible' ? null : det.tier;
  const tierClass =
    det.tier === 'detected'
      ? 'text-emerald-400/90'
      : det.tier === 'likely'
        ? 'text-teal-400/90'
        : 'text-amber-400/90';

  return (
    <div
      ref={refCb}
      data-stage={stage}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      className={`group relative rounded-md border cursor-pointer overflow-hidden transition-all duration-200 ${
        selected
          ? 'border-teal-400/70 bg-slate-800/90 shadow-[0_0_0_2px_rgba(45,212,191,0.2)]'
          : 'border-slate-700/60 bg-slate-800/50 hover:border-slate-500/70 hover:bg-slate-800/80'
      } ${dimmed ? 'opacity-25' : 'opacity-100'}`}
    >
      <div className="px-2.5 py-2">
        <div className="flex items-center justify-between gap-2 min-w-0">
          <span className="text-[12px] font-semibold text-slate-100 truncate" title={node.name}>
            {node.name}
          </span>
          <span className="text-[9px] font-mono text-slate-500 flex-shrink-0">
            {Math.round(det.confidence * 100)}%
          </span>
        </div>
        <div className="flex items-center justify-between gap-2 mt-1">
          <span className="text-[9.5px] uppercase tracking-wider text-slate-500 truncate">
            {node.category.replace(/_/g, ' ')}
          </span>
          {tierLabel && (
            <span
              className={`text-[9.5px] uppercase tracking-wider font-semibold ${tierClass} flex-shrink-0`}
            >
              {tierLabel}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

interface GapCardProps {
  node: KBNode;
  priority?: string;
  stage: SdlcStage;
  selected: boolean;
  dimmed: boolean;
  onEnter: (e: React.MouseEvent) => void;
  onLeave: () => void;
  onClick: () => void;
  refCb: (el: HTMLDivElement | null) => void;
}

export function GapCard({
  node,
  priority,
  stage,
  selected,
  dimmed,
  onEnter,
  onLeave,
  onClick,
  refCb,
}: GapCardProps) {
  const pri = priority || 'medium';
  const priTone =
    pri === 'high'
      ? 'text-rose-400/90'
      : pri === 'low'
        ? 'text-emerald-400/90'
        : 'text-amber-400/90';

  return (
    <div
      ref={refCb}
      data-stage={stage}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      className={`group relative rounded-md border-[1.5px] border-dashed cursor-pointer overflow-hidden transition-all duration-200 ${
        selected
          ? 'border-teal-400 bg-teal-500/10'
          : 'border-slate-600/60 bg-transparent hover:border-teal-400/60 hover:bg-teal-500/[0.04]'
      } ${dimmed ? 'opacity-25' : 'opacity-100'}`}
    >
      <div className="px-2.5 py-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Plus className="w-3 h-3 flex-shrink-0 text-slate-500 group-hover:text-teal-400 transition-colors" />
          <span className="text-[12px] font-semibold text-slate-200 truncate" title={node.name}>
            {node.name}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2 mt-1">
          <span className="text-[9.5px] uppercase tracking-wider text-slate-500">suggested</span>
          <span className={`text-[9.5px] uppercase tracking-wider font-semibold ${priTone}`}>
            {pri}
          </span>
        </div>
      </div>
    </div>
  );
}

export function EmptySlot() {
  return (
    <div className="rounded-md border border-dashed border-slate-700/30 bg-transparent px-2.5 py-4 text-center">
      <AlertTriangle className="w-3 h-3 text-slate-600 mx-auto mb-1" />
      <div className="text-[10px] text-slate-600 italic">Nothing here</div>
    </div>
  );
}

export function LegendSwatch({
  color,
  dash,
  label,
}: {
  color: string;
  dash?: string;
  label: string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <svg width="20" height="8">
        <path
          d="M0,4 L20,4"
          stroke={color}
          strokeWidth={1.5}
          strokeDasharray={dash}
          strokeOpacity={0.8}
        />
      </svg>
      <span className="text-slate-400">{label}</span>
    </div>
  );
}
